<template lang="pug">
  div
    v-divider.mt-2
    .text-lg.font-medium.neutral700--text.mt-4 Panel payment method
    .text-sm.neutral700--text Add, edit, remove or change status of your panels.

    v-row.mt-6.pb-2.pb-md-0(no-gutters)
      v-col(cols="12" md="7")
        v-tabs(v-model="tabs" color="basic700" :height="44" :grow="isMobileScreen")
          v-tab.text-sm.font-semibold.text-none.px-4 {{ $t('all') }}
          v-tab.text-sm.font-semibold.text-none.px-4 Active
          v-tab.text-sm.font-semibold.text-none.px-4 Archived
      v-col(cols="12" md="5" align="end")
        .d-flex.align-center
          v-spacer
          v-btn(@click="clickSearch" icon color="neutral500")
            v-icon(size="20") $yzi-search-sm
          v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
          v-btn.button-sm.ml-4(color="neutral700" dark depressed @click="addNewPanels") Add panel
    v-divider

    .data-table
      v-data-table.mt-sm-4.pb-6(:headers="headers" :items="panels" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" :disable-sort="isMobileScreen" :header-props="{ sortIcon: '$yzi-triangle' }")
        //- Name
        template(v-slot:item.name="{ item }")
          v-tooltip(top color="black" content-class="black tooltip-top-arrow")
            template(#activator="{ on, attrs }")
              div(v-bind="attrs" v-on="on")
                div.my-3(style="max-width: 180px;")
                  .text-sm.font-medium.neutral700--text.cursor-help.truncate {{ item.name }}
                  .text-xs.neutral700--text {{ item.code }}
            div(style="max-width: 30vw")
              .text-sm.font-medium {{ item.name }}
        //- link
        template(v-slot:item.link="{ item }")
          template(v-if="item.panel.display_remarks")
            .text-sm.neutral700--text {{ item.panel.remarks }}
          template(v-else-if="item.panel.verification_link")
            div(style="word-break: break-word; max-width: 300px")
              a.text-sm.neutral700--text(style="text-underline-offset: 3px;" target="_blank" :href="item.panel.verification_link") {{ item.panel.verification_link }}
        //- price tier
        template(v-slot:item.price_tier="{ item }")
          div.d-flex.align-center
            template(v-for="ptarray in filteredArrays(item.price_tiers)")
              y-chip(:label="ptarray.name" size="sm" chip-class="pa-1 mr-2" :key="ptarray.id" outlined)
            v-tooltip(v-if="item.price_tiers.length !== filteredArrays(item.price_tiers).length" top color="neutral700")
              template(#activator="{ on, attrs }")
                div(v-bind="attrs" v-on="on")
                  y-chip.cursor-help(v-bind="attrs" v-on="on" :label="`+${item.price_tiers.length - filteredArrays(item.price_tiers).length} more`" size="sm" chip-class="pa-1" outlined)
              div
                div(v-for="(pricetier, index) in item.price_tiers" :key="pricetier.id") {{ index + 1 }}. {{ pricetier.name }}
        //- status
        template(v-slot:item.status="{ item }")
          div
            y-switch.mb-n3.ml-n3(v-model="item.enabled" @change="updateStatus(item, item.enabled)")
        //- actions
        template(v-slot:item.actions="{ item }")
          div.d-flex
            v-spacer
            v-menu(offset-x left attach rounded="lg")
              template(#activator="{ on }")
                v-btn.mr-3(icon small v-on="on")
                  v-icon mdi-dots-horizontal
              v-list.py-0(width="100")
                v-list-item(@click="editItem(item)")
                  v-list-item-title.text-sm.neutral700--text Edit
                v-divider
                v-list-item(@click="deleteItem(item)")
                  v-list-item-title.text-sm.danger500--text Delete

    div.d-flex.align-center(v-if="totalCount > pageSize")
      v-spacer
      yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top @input="pageChanged")

    .dialogs
      add-panel-dialog(v-if="openAddPanelsDialog" :open.sync="openAddPanelsDialog" :parent-panel="selectedPanel" @closed="closed" @reset="reset")
      delete-confirmation-dialog(v-if="openDeleteDialog" :open.sync="openDeleteDialog" :loading="isLoading" @cancel="closed" @delete="confirmDelete")
        .text-lg.font-medium Are you sure you want to delete this panel?
        .text-md.mt-4 This action will be done immediately and can’t be undone. Click “Confirm” to proceed

</template>

<script>
import { debounce } from '@/utils'
import { mapGetters, mapActions } from 'vuex'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'
import AddPanelDialog from '@/components/clinic/panel/dialogs/AddPanelDialog.vue'

export default {
  components: { AddPanelDialog, DeleteConfirmationDialog },
  data: () => ({
    showSearchField: false,
    isFetching: false,
    isLoading: false,
    openAddPanelsDialog: false,
    openDeleteDialog: false,
    selectedPanel: null,
    tabs: 0,
    pageSize: 10,
    searchText: '',
    totalCount: 0,
    currentPage: 1,
    panels: [],
    headers: [
      { text: 'Name', value: 'name', enable: true, width: '180px' },
      { text: 'Verify link', value: 'link', enable: true },
      { text: 'Price tier', value: 'price_tier', enable: true },
      { text: 'Status', value: 'status', enable: true, width: '100' },
      { text: 'Actions', value: 'actions', enable: true, class: 'text-end', width: '100', sortable: false },
    ]
  }),
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
  },
  watch: {
    tabs () {
      this.searchText = null
      this.currentPage = 1
      this.fetchPanels()
    },
    pageSize () {
      this.currentPage = 1
      this.fetchPanels()
    }
  },
  mounted () {
    this.fetchPanels()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchPanels () {
      this.isFetching = true
      let params = {
        page_size: this.pageSize,
        page: this.currentPage,
        company: this.company.id,
        channel: 'panel'
      }
      if (this.tabs === 1) { params.enabled = true }
      if (this.tabs === 2) { params.enabled = false }
      if (this.searchText) { params.search = this.searchText }
      this.$axios.get(`/payment_methods/`, { params })
        .then(resp => {
          this.panels = resp.data.results
          this.totalCount = resp.data.count
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    updateStatus (item, status) {
      this.isFetching = true
      this.$axios.patch(`/payment_methods/${item.id}`, { enabled: status })
        .then(() => {
          this.reset()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          this.isFetching = false
        })
    },
    confirmDelete () {
      this.isLoading = true
      this.$axios.delete(`/payment_methods/${this.selectedPanel.id}`)
        .then(() => {
          this.reset()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    queryChanged: debounce(function () {
      this.currentPage = 1
      this.fetchPanels()
    }, 750),
    pageChanged () {
      this.fetchPanels()
    },
    clickSearch () {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true 
      }
    },
    blurSearch () {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    addNewPanels () {
      this.openAddPanelsDialog = true
    },
    editItem (item) {
      this.selectedPanel = item
      this.openAddPanelsDialog = true
    },
    deleteItem (item) {
      this.selectedPanel = item
      this.openDeleteDialog = true
    },
    reset () {
      this.currentPage = 1
      this.fetchPanels()
      this.selectedPanel = null
      this.openDeleteDialog = false
      this.openAddPanelsDialog = false
    },
    closed () {
      this.selectedPanel = null
      this.openDeleteDialog = false
      this.openAddPanelsDialog = false
    },
    filteredArrays (arrays) {
      if (arrays.length > 1) {
        let newArray = []
        let count = 0
        arrays.forEach((o, idx) => {
          count += o.name.length
          if (idx === 0 || count <= 25) {
            newArray.push(o)
          }
        })
        return newArray
      }
      return arrays
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-width {
  width: 50px !important;
}
</style>